<script setup>
import { defineProps, ref, computed, defineEmits } from "vue";
import FileUpload from "primevue/fileupload";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Dialog from "primevue/dialog";
import Tag from "primevue/tag";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { dashboardAPI } from "../service/api.js";
import Chip from "primevue/chip";
import Fieldset from "primevue/fieldset";
import VendorDetailDialog from "./VendorDetailDialog.vue";
import EORApprovedEmailsImport from "./EORApprovedEmailsImport.vue";
import { useCurrentUser } from "../composables/useCurrentUser";

const emit = defineEmits(["update", "page", "sort", "filter", "clear-filters"]);
const confirm = useConfirm();
const toast = useToast();
const { hasRole } = useCurrentUser();

const props = defineProps({
  vendors: {
    type: Array,
    required: true,
  },
  unknownVendorIds: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    default: () => ({ page: 1, pageSize: 50, totalItems: 0, totalPages: 0 }),
  },
  filterModel: {
    type: Object,
    default: () => ({}),
  },
  sortBy: {
    type: String,
    default: "name",
  },
  sortOrder: {
    type: String,
    default: "asc",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

// PrimeVue DataTable expects sortOrder as number: 1 = asc, -1 = desc
const sortOrderNum = computed(() => (props.sortOrder === "asc" ? 1 : -1));

const onPage = (event) => {
  emit("page", event);
};

const onSort = (event) => {
  emit("sort", event);
};

function emitFilter(updates) {
  emit("filter", { ...props.filterModel, ...updates });
}

const paymentTypeOptions = [
  { label: "All", value: null },
  { label: "ACH/EFT", value: "ACH/EFT" },
  { label: "Instant Virtual Card", value: "Instant Virtual Card" },
];

const batchPaymentPreferenceOptions = [
  { label: "All", value: null },
  { label: "Batch Payments", value: "Batch Payments" },
  { label: "Individual Payments", value: "Individual Payments" },
];

const vendorTypeOptions = [
  { label: "All", value: null },
  { label: "PRIMARY", value: "PRIMARY" },
];

// Terra Phase 3 (ClickUp 86bb8mc05) — payment eligibility, separate from
// vendor existence. See docs/api's vendors.payment_eligibility_status.
// No "Duplicate Suspected" option here: no backend code path ever sets a
// vendors.payment_eligibility_status row to duplicate_suspected — that
// status only ever lives on vendor_source_mappings.status. The actual
// duplicate-review queue is the separate Vendor Review tab
// (GET /vendor-source-mappings). Including it here would filter to an
// always-empty result and wrongly imply there are no suspected duplicates.
const eligibilityOptions = [
  { label: "All", value: null },
  { label: "Digital Enabled", value: "digital_enabled" },
  { label: "Pending Enrollment", value: "pending_enrollment" },
  { label: "Check Required", value: "check_required" },
  { label: "Unknown", value: "unknown" },
  { label: "Inactive", value: "inactive" },
];

const eligibilitySeverity = (status) => {
  switch (status) {
    case "digital_enabled":
      return "success";
    case "pending_enrollment":
      return "warn";
    case "duplicate_suspected":
      return "danger";
    case "inactive":
    case "check_required":
    case "unknown":
    default:
      return "secondary";
  }
};

const approvePaymentTypeOptions = [
  { label: "ACH/EFT", value: "ACH/EFT" },
  { label: "Instant Virtual Card", value: "Instant Virtual Card" },
];
const approveBatchPreferenceOptions = [
  { label: "Batch Payments", value: "Batch Payments" },
  { label: "Individual Payments", value: "Individual Payments" },
];

const showApproveDialog = ref(false);
const approveTarget = ref(null);
const approveForm = ref({ payment_type: null, batch_payment_preference: null });
const isApproving = ref(false);

const openApproveDialog = (row) => {
  approveTarget.value = row;
  approveForm.value = { payment_type: null, batch_payment_preference: null };
  showApproveDialog.value = true;
};

const submitApprove = async () => {
  if (!approveForm.value.payment_type || !approveForm.value.batch_payment_preference) {
    toast.add({
      severity: "warn",
      summary: "Missing fields",
      detail: "Payment type and batch preference are both required to approve a vendor.",
      life: 4000,
    });
    return;
  }
  isApproving.value = true;
  try {
    await dashboardAPI.approveVendor(approveTarget.value.id, approveForm.value);
    toast.add({
      severity: "success",
      summary: "Vendor Approved",
      detail: `${approveTarget.value.name} is now digital-enabled; held payments have been requeued.`,
      life: 4000,
    });
    showApproveDialog.value = false;
    emit("update");
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Approve Failed",
      detail: err.response?.data || err.message || "Failed to approve vendor",
      life: 5000,
    });
  } finally {
    isApproving.value = false;
  }
};

const handleRejectVendor = (row) => {
  confirm.require({
    message: `Reject ${row.name}? Any payments held for this vendor will be terminally failed — Terra would need to resubmit them under a new transaction id.`,
    header: "Reject Vendor",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "Reject",
    acceptClass: "p-button-danger",
    accept: async () => {
      try {
        await dashboardAPI.rejectVendor(row.id, {});
        toast.add({ severity: "success", summary: "Vendor Rejected", detail: `${row.name} was rejected.`, life: 3000 });
        emit("update");
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Reject Failed",
          detail: err.response?.data || err.message || "Failed to reject vendor",
          life: 5000,
        });
      }
    },
  });
};

const isResolving = ref(false);
const showVendorDialog = ref(false);
const selectedVendorId = ref(null);

const openEditDialog = (vendorId) => {
  selectedVendorId.value = vendorId;
  showVendorDialog.value = true;
};

const handleDeleteVendor = (row) => {
  confirm.require({
    message: "Delete this vendor? This will only succeed if the vendor has no associated transactions.",
    header: "Delete Vendor",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    acceptClass: "p-button-danger",
    accept: async () => {
      try {
        await dashboardAPI.deleteVendor(row.id);
        toast.add({
          severity: "success",
          summary: "Vendor Deleted",
          detail: "Vendor has been deleted.",
          life: 3000,
        });
        emit("update");
      } catch (err) {
        const msg =
          err.response?.status === 409
            ? err.response?.data?.error || "Cannot delete vendor: vendor has associated transactions."
            : err.response?.data || err.message || "Failed to delete vendor";
        toast.add({
          severity: "error",
          summary: "Delete Failed",
          detail: msg,
          life: 5000,
        });
      }
    },
  });
};

const onVendorDialogUpdate = () => {
  emit("update");
};

const handleFileUpload = async (event) => {
  await dashboardAPI.uploadVendorFile(event.files[0]);
  emit("update");
};

const handleResolveVendors = () => {
  confirm.require({
    message: "This will clear and re-resolve all vendor IDs for all transactions. Are you sure you want to proceed?",
    header: "Resolve All Vendor IDs",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "Resolve",
    accept: async () => {
      isResolving.value = true;
      try {
        const response = await dashboardAPI.resolveVendorIds();
        const data = response.data;

        const skippedMsg = data.stats.skipped_duplicates > 0
          ? ` ${data.stats.skipped_duplicates} skipped (duplicates).`
          : '';

        toast.add({
          severity: "success",
          summary: "Vendor Resolution Complete",
          detail: `Resolved ${data.stats.total_resolved} transactions (${data.stats.resolved_by_carepay_id} by Carepay ID, ${data.stats.resolved_by_tax_id} by Tax ID).${skippedMsg} ${data.stats.unresolved} remain unresolved.`,
          life: 5000,
        });

        emit("update");
      } catch (error) {
        console.error("Error resolving vendors:", error);
        toast.add({
          severity: "error",
          summary: "Resolution Failed",
          detail: error.response?.data?.error || error.message || "Failed to resolve vendor IDs",
          life: 5000,
        });
      } finally {
        isResolving.value = false;
      }
    },
  });
};
</script>

<template>
  <div>
    <DataTable
      size="large"
      :value="vendors"
      dataKey="id"
      lazy
      paginator
      filterDisplay="row"
      :first="(pagination.page - 1) * pagination.pageSize"
      :rows="pagination.pageSize"
      :totalRecords="pagination.totalItems"
      :sortField="sortBy"
      :sortOrder="sortOrderNum"
      :loading="loading"
      @page="onPage"
      @sort="onSort"
    >
      <template #header>
        <div class="header">
          <Button
            label="Clear filters"
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            @click="emit('clear-filters')"
          />
          <Fieldset legend="Unknown Carepay Vendor Ids" class="unknownvendors">
            <Chip
              v-for="id in unknownVendorIds"
              :label="id"
              :key="id"
              class="mr-2"
            />
          </Fieldset>
          <FileUpload
            mode="basic"
            name="vendorFile"
            @select="handleFileUpload"
            customUpload
            :auto="true"
            chooseLabel="Upload Vendor File"
          />
          <EORApprovedEmailsImport v-if="hasRole('role_admin')" />
          <Button
            label="Resolve All Vendors"
            icon="pi pi-sync"
            severity="danger"
            size="small"
            @click="handleResolveVendors"
            :loading="isResolving"
          />
        </div>
      </template>
      <template #empty> No vendors found. </template>
      <Column header="Actions" :exportable="false" style="min-width: 10rem">
        <template #body="slotProps">
          <Button
            icon="pi pi-pencil"
            severity="secondary"
            text
            rounded
            size="small"
            title="Edit"
            @click="openEditDialog(slotProps.data.id)"
          />
          <Button
            icon="pi pi-trash"
            severity="danger"
            text
            rounded
            size="small"
            title="Delete"
            @click="handleDeleteVendor(slotProps.data)"
          />
          <template v-if="slotProps.data.payment_eligibility_status === 'pending_enrollment'">
            <Button
              icon="pi pi-check"
              severity="success"
              text
              rounded
              size="small"
              title="Approve"
              @click="openApproveDialog(slotProps.data)"
            />
            <Button
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              size="small"
              title="Reject"
              @click="handleRejectVendor(slotProps.data)"
            />
          </template>
        </template>
      </Column>
      <Column sortable field="id" header="Vendor ID">
        <template #filter>
          <InputText
            :modelValue="filterModel?.id ?? ''"
            type="text"
            placeholder="Search ID"
            @update:modelValue="(v) => emitFilter({ id: v })"
          />
        </template>
      </Column>
      <Column sortable field="carepay_vendor_id" header="Carepay Vendor ID">
        <template #filter>
          <InputText
            :modelValue="filterModel?.carepay_vendor_id ?? ''"
            type="text"
            placeholder="Search Carepay ID"
            @update:modelValue="(v) => emitFilter({ carepay_vendor_id: v })"
          />
        </template>
      </Column>
      <Column sortable field="name" header="Name">
        <template #filter>
          <InputText
            :modelValue="filterModel?.name ?? ''"
            type="text"
            placeholder="Search name"
            @update:modelValue="(v) => emitFilter({ name: v })"
          />
        </template>
      </Column>
      <Column sortable field="city" header="City">
        <template #filter>
          <InputText
            :modelValue="filterModel?.city ?? ''"
            type="text"
            placeholder="Search city"
            @update:modelValue="(v) => emitFilter({ city: v })"
          />
        </template>
      </Column>
      <Column sortable field="state" header="State">
        <template #filter>
          <InputText
            :modelValue="filterModel?.state ?? ''"
            type="text"
            placeholder="Search state"
            @update:modelValue="(v) => emitFilter({ state: v })"
          />
        </template>
      </Column>
      <Column sortable field="payment_type" header="Payment Type">
        <template #filter>
          <Select
            :modelValue="filterModel?.payment_type || null"
            :options="paymentTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            @update:modelValue="(v) => emitFilter({ payment_type: v ?? '' })"
          />
        </template>
      </Column>
      <Column sortable field="batch_payment_preference" header="Batch Preference">
        <template #filter>
          <Select
            :modelValue="filterModel?.batch_payment_preference || null"
            :options="batchPaymentPreferenceOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            @update:modelValue="(v) => emitFilter({ batch_payment_preference: v ?? '' })"
          />
        </template>
      </Column>
      <Column field="email" header="Email">
        <template #filter>
          <InputText
            :modelValue="filterModel?.email ?? ''"
            type="text"
            placeholder="Search email"
            @update:modelValue="(v) => emitFilter({ email: v })"
          />
        </template>
      </Column>
      <Column field="vendor_type" header="Vendor Type">
        <template #filter>
          <Select
            :modelValue="filterModel?.vendor_type || null"
            :options="vendorTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            @update:modelValue="(v) => emitFilter({ vendor_type: v ?? '' })"
          />
        </template>
      </Column>
      <Column field="payee_type" header="Payee Type" />
      <Column sortable field="payment_eligibility_status" header="Eligibility">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.payment_eligibility_status"
            :severity="eligibilitySeverity(slotProps.data.payment_eligibility_status)"
          />
        </template>
        <template #filter>
          <Select
            :modelValue="filterModel?.payment_eligibility_status || null"
            :options="eligibilityOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            @update:modelValue="(v) => emitFilter({ payment_eligibility_status: v ?? '' })"
          />
        </template>
      </Column>
    </DataTable>
    <Dialog v-model:visible="showApproveDialog" header="Approve Vendor" modal style="width: 28rem">
      <p v-if="approveTarget">
        Enable digital payments for <strong>{{ approveTarget.name }}</strong> and requeue any
        payments held for it.
      </p>
      <div class="approve-field">
        <label>Payment Type</label>
        <Select
          v-model="approveForm.payment_type"
          :options="approvePaymentTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select payment type"
          style="width: 100%"
        />
      </div>
      <div class="approve-field">
        <label>Batch Payment Preference</label>
        <Select
          v-model="approveForm.batch_payment_preference"
          :options="approveBatchPreferenceOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select batch preference"
          style="width: 100%"
        />
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" outlined @click="showApproveDialog = false" />
        <Button label="Approve" :loading="isApproving" @click="submitApprove" />
      </template>
    </Dialog>
    <VendorDetailDialog
      v-model:visible="showVendorDialog"
      :vendor-id="selectedVendorId"
      @update="onVendorDialogUpdate"
    />
    <ConfirmDialog />
  </div>
</template>

<style scoped>
.header {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.unknownvendors {
  display: flex;
  flex-wrap: wrap;
  margin-right: 1rem;
}
.approve-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
}
/* Base styling for all tables */
:deep(.p-datatable) {
  margin-bottom: 1rem;
}
</style>
